import { z } from "zod";

import { EXPENSE_CATEGORIES, type ExpenseCategoryValue } from "../catalog/expense-categories.js";
import { CalendarDateSchema, MONEY_LIMIT, hasAtMostTwoDecimals } from "./common.js";

/**
 * DTOs for the expenses context — recurring expenses, cards, and the monthly settle-up.
 *
 * Same contract role as `savings.ts` and `properties.ts`: use cases return these, route handlers pass
 * them through unchanged, and `@gladis/api-client` derives its types from the same schemas, so a
 * contract change is a compile error on both sides at once.
 *
 * FOUR REPRESENTATION DECISIONS ARE ENCODED HERE, AND ALL FOUR ARE LOAD-BEARING.
 *
 * **Money crosses as a number of EUROS**, per savings' header. Totals are computed as `Valuation`
 * (integer cents) and converted once, at the edge.
 *
 * **`startDate` crosses as `YYYY-MM-DD`**, never an ISO timestamp. This is the third context to need
 * `CalendarDateSchema` and the exposure is larger than in the first two: a one-day shift on a
 * `valuation_date` mislabels a row, whereas a one-day shift on `start_date` decides whether the
 * expense counts towards the household's monthly total AT ALL.
 *
 * **`lastFour` is a STRING.** A number arrives as `42` and the leading zeros of `0042` are gone
 * before any validation runs. It is four characters of an account identifier, held so two cards from
 * the same bank can be told apart — the full number is deliberately not held anywhere, because
 * nothing in this application initiates a payment and the local database is dumped to a plaintext
 * file as part of the normal workflow.
 *
 * **Split percentages cross as numbers with at most two decimals**, matching `numeric(5,2)`. The
 * domain converts them to integer BASIS POINTS, because a float sum of `33.33 + 33.33 + 33.34` is
 * not reliably `100` and the sum-to-exactly-100% rule has to be exact.
 *
 * NOTHING HERE ACCEPTS A DISPLAY NAME. Not `memberDisplayName` on a split, not `ownerDisplayName` on
 * a card. Both are snapshotted server-side from the member list the composition root already
 * resolved: a client-supplied name would let a caller write a name for a user they are not permitted
 * to enumerate, and for a share belonging to a member who has left the household the stored name is
 * the only correct source.
 */

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

/**
 * The six intervals. Their monthly-equivalent ratios live in the domain, not here — the wire needs
 * to know which interval, and the arithmetic is not the wire's business.
 */
export const RecurrenceIntervalSchema = z.enum([
  "weekly",
  "every_4_weeks",
  "monthly",
  "quarterly",
  "half_yearly",
  "yearly",
]);
export type RecurrenceIntervalValue = z.infer<typeof RecurrenceIntervalSchema>;

/**
 * DERIVED from the catalogue, never restated (FR-022).
 *
 * The cast is the one concession. `z.enum` wants a non-empty tuple and `Array.prototype.map` returns
 * an array, so the literal-union type survives only if it is asserted back. The alternative — writing
 * the seventeen values here as a second literal list — is precisely the duplication the catalogue
 * exists to prevent, and it is the copy nobody would suspect, because it would sit one import away
 * from the source. An architecture test fails if a second hand-written list appears anywhere.
 */
const CATEGORY_VALUES = EXPENSE_CATEGORIES.map((category) => category.value) as unknown as [
  ExpenseCategoryValue,
  ...ExpenseCategoryValue[],
];

export const ExpenseCategorySchema = z.enum(CATEGORY_VALUES);

/**
 * `joint` or `individual` — the same convention savings accounts use, and deliberately NOT the same
 * type. The database enum is `card_ownership`, separate from `savings_account_ownership`: the values
 * coinciding costs nothing, whereas sharing one type would mean a value added for cards appearing in
 * savings' vocabulary.
 */
export const CardOwnershipSchema = z.enum(["joint", "individual"]);
export type CardOwnershipValue = z.infer<typeof CardOwnershipSchema>;

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/** The magnitude a `numeric(15,2)` column holds. Rejected at the boundary as well as in the domain. */
export const EXPENSE_AMOUNT_LIMIT = MONEY_LIMIT;

/**
 * Strictly positive. Unlike a savings balance, an expense of zero is meaningless and the column says
 * so (`check (amount > 0)`) — but the refusal belongs here and in the domain first, so the member
 * reads a sentence rather than a relayed constraint name.
 */
export const ExpenseAmountSchema = z
  .number()
  .finite()
  .positive()
  .max(EXPENSE_AMOUNT_LIMIT)
  .refine(hasAtMostTwoDecimals, { message: "At most two decimal places." });

/**
 * A share of responsibility: greater than zero, at most 100, at most two decimals.
 *
 * Zero is refused rather than accepted-and-ignored. A member with no share is ABSENT from the split,
 * which is also what the column requires (`check (percentage > 0 and percentage <= 100)`) — a stored
 * zero would be a member the household believes is responsible for nothing, which is a different
 * claim from not being on the list.
 */
export const SplitPercentageSchema = z
  .number()
  .finite()
  .positive()
  .max(100)
  .refine(hasAtMostTwoDecimals, { message: "At most two decimal places." });

/**
 * Between two and eleven upper-case letters and digits — long enough for a BIC, short enough that
 * nothing resembling an account number fits. Four is the Dutch case (`INGB`, `RABO`, `ABNA`).
 *
 * Upper-casing happens server-side rather than being demanded of the client, so a member typing
 * `ingb` is helped rather than corrected.
 */
export const BankCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9]{2,11}$/, "Two to eleven letters or digits.");

/** Exactly four digits, as a string, so `0042` survives. See the header. */
export const LastFourSchema = z
  .string()
  .trim()
  .regex(/^\d{4}$/, "Exactly four digits.");

// ---------------------------------------------------------------------------
// The response — `GET /api/expenses`
// ---------------------------------------------------------------------------

/*
 * ==========================================================================================
 * ONE SCHEMA FOR THE WHOLE PAGE, AND A TEST THAT PARSES A REAL RESPONSE THROUGH IT
 * ==========================================================================================
 *
 * What was here before was a set of eleven response schemas that NOTHING imported and that had stopped being
 * true: `expenses` where the payload says `rows`, an optional `derivedLines` section that became rows in the
 * table, no `byMember`, `monthlyTotal` where the payload says `monthlyTotalInEuros`. A schema nobody reads
 * cannot be wrong loudly, so it was wrong quietly, and the next person to reach for it would have believed it.
 *
 * The response contract is otherwise declared THREE times and checked never: `ExpensesPage` in the domain use
 * case, this file, and the `ExpensesPageDto` interface the SPA hand-writes. TypeScript connects none of them —
 * the client's interface describes JSON it has no compile-time relationship with — so renaming a field in the
 * domain would leave the SPA reading `undefined` and every total rendering as `€ 0,00`.
 *
 * So this schema is not documentation and not a validator on the hot path. It is the assertion:
 * `expenses-list.test.ts` parses the route's real response through it, and a domain field that changes name or
 * shape fails there rather than in a browser. Keeping it honest costs one edit in the same commit as the
 * change that broke it.
 *
 * A `Date` in the domain arrives here as an ISO STRING, because that is what `JSON.stringify` does to it. Both
 * `archivedAt` fields are that, and it is the one place where the wire shape and the domain shape genuinely
 * differ.
 *
 * EVERY OBJECT IS `strictObject`, top level and nested. `z.object` STRIPS an unknown key, which would let a
 * field added to the domain and never published here pass the test silently — and a field added but not
 * published is as invisible to the SPA as one renamed. Strict makes both directions fail, and the cost is one
 * edit in the same commit as the change: a new domain field is a new line here.
 */

export const CardDtoSchema = z.strictObject({
  id: z.uuid(),
  name: z.string().min(1).max(100),
  ownership: CardOwnershipSchema,
  /** Null when joint, and also once an individual owner has deleted their account. */
  ownerId: z.uuid().nullable(),
  /**
   * The owner's CURRENT name while they are an active member; the snapshot once they are not.
   *
   * Resolved server-side, so the client never sees the choice and cannot get the fallback wrong —
   * `CardEntity.ownerLabel` owns it, and `SavingsAccountDto.ownerLabel` states the same rule for the same pair
   * of facts (FR-023, FR-024).
   *
   * It used to be the snapshot unconditionally, which is right for a departed owner and wrong for a present
   * one: a member who corrected their display name saw it change everywhere except on their own cards. The
   * stored `owner_display_name` is unchanged and still the fallback — removing a member leaves their
   * `public.users` row intact, but a member DELETING THEIR ACCOUNT does not: `owner_id` goes null, and after
   * that the snapshot is all that identifies them. `savings_accounts.owner_display_name` exists for the same
   * reason, and its migration records that a column added later cannot recover a name already dropped.
   */
  ownerName: z.string().nullable(),
  /**
   * `"INGB •••• 4488"`, or one half of it, or NULL when neither half is set.
   *
   * Null rather than an empty string or a bare run of bullets: a card with no identifier shows no identifier
   * line at all. Assembled server-side so the four cases are decided in one place.
   */
  identifier: z.string().nullable(),
  /** The same identifier as words, for a screen reader: "INGB, ending 4488". */
  accessibleIdentifier: z.string().nullable(),
  bankCode: z.string().nullable(),
  lastFour: z.string().nullable(),
  /** The sum of the monthly equivalents of the ACTIVE, STARTED expenses bound to this card (FR-046). */
  monthlyTotalInEuros: z.number(),
  /** Sent as its own field rather than left to the client to derive from `ownership`. */
  isJoint: z.boolean(),
  archivedAt: z.iso.datetime().nullable(),
});
export type CardDto = z.infer<typeof CardDtoSchema>;

export const ExpenseSplitDtoSchema = z.strictObject({
  /** Null once the member's row is gone entirely — the name below is then all there is. */
  memberId: z.uuid().nullable(),
  /** Snapshotted when the share was written. For a frozen share it is the only correct source. */
  memberDisplayName: z.string(),
  percentage: z.number(),
  /** True when the share belongs to somebody who is no longer a current active member (FR-048). */
  isFrozen: z.boolean(),
});
export type ExpenseSplitDto = z.infer<typeof ExpenseSplitDtoSchema>;

export const ExpenseCardRefSchema = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  identifier: z.string().nullable(),
  /** So the row can mark a card that no longer appears in the picker (FR-044). */
  isArchived: z.boolean(),
});

/**
 * One row of the table — an expense the household entered, or a mortgage part.
 *
 * `kind` is the discriminator every renderer branches on. A `derived` row's figures are computed from an
 * amortisation schedule on every read: it cannot be edited or archived here, and what CAN be set on it is who
 * pays. See `ExpenseRow` in the domain for the full argument.
 */
export const ExpenseRowDtoSchema = z.strictObject({
  kind: z.enum(["expense", "derived"]),
  id: z.uuid(),
  description: z.string().min(1).max(200),
  amountInEuros: z.number(),
  interval: RecurrenceIntervalSchema,
  category: ExpenseCategorySchema,
  startDate: CalendarDateSchema,
  /** Rounded to the cent. Every total is a sum of these. */
  monthlyEquivalentInEuros: z.number(),
  hasStarted: z.boolean(),
  archivedAt: z.iso.datetime().nullable(),
  card: ExpenseCardRefSchema.nullable(),
  /** Empty means household-shared with no individual attribution. */
  splits: z.array(ExpenseSplitDtoSchema),
  /** On a mortgage row: how much of the payment is interest. Null on an entered expense. */
  interestInEuros: z.number().nullable(),
  /** On a mortgage row: the mortgage part, which its attribution is keyed on. Null otherwise. */
  sourceId: z.uuid().nullable(),
});
export type ExpenseRowDto = z.infer<typeof ExpenseRowDtoSchema>;

/**
 * A group of rows, collapsed into one line carrying a monthly total.
 *
 * ==========================================================================================
 * `kind` IS `"group"`, NOT `"vehicleGroup"`, AND THAT IS THE WHOLE DESIGN
 * ==========================================================================================
 *
 * The expenses interface must not learn what a vehicle is. `sourceId` is opaque here in exactly the
 * sense `ExpenseRowDto.sourceId` already is for a mortgage part — "this context does not model what
 * produced it" — so `properties` could supply groups later with **zero change to this schema**. One
 * word, and it is the second use of a seam feature 009 already cut.
 *
 * ==========================================================================================
 * A SEPARATE SCHEMA, NOT A THIRD VALUE IN `ExpenseRowDtoSchema`'s `kind`
 * ==========================================================================================
 *
 * A group has no `splits`, no `card`, no `startDate`, no `category` and no `interestInEuros`.
 * Folding it into the existing object would hand it eight meaningless nulls and make every
 * consumer's narrowing weaker.
 *
 * ==========================================================================================
 * NESTED, NOT FLAT — AND THAT IS WHAT STOPS THE TOTAL DOUBLING
 * ==========================================================================================
 *
 * `monthlyTotalInEuros` is a sum over `rows`. If groups and their children were both top level, the
 * same cost would be counted twice — silently, with nothing on the page looking broken. Nesting
 * makes that IMPOSSIBLE BY CONSTRUCTION rather than by care.
 *
 * `children` is typed as `ExpenseRowDtoSchema` and never the union, so **a group cannot contain a
 * group**: a car inside a house group is not a thing, and forbidding it keeps the type
 * non-recursive.
 */
export const ExpenseGroupRowDtoSchema = z.strictObject({
  kind: z.literal("group"),
  /** The record that produced this group. Opaque: expenses does not model what it is. */
  sourceId: z.uuid(),
  /** What the row is called. Supplied by whatever produced it. */
  label: z.string().min(1),
  /** Rendered in the slot an ordinary row's category occupies — "4 costs". */
  childCount: z.number().int().positive(),
  /**
   * Always `monthly`, and NOT a fudge.
   *
   * A group's figure IS a monthly equivalent by construction, so `monthly` describes the number
   * printed beside it. "€284.17 monthly" is true and reads exactly like every other row — which is
   * why `sortRows` needs no special case, no NULLS-LAST rule and no third sort state.
   */
  interval: z.literal("monthly"),
  amountInEuros: z.number(),
  monthlyEquivalentInEuros: z.number(),
  /**
   * Never empty. FR-037 — "no children, no row" — encoded as a type rather than a rule somebody has
   * to remember: a group with nothing in it cannot be represented, so it cannot be rendered at €0.00.
   */
  children: z.array(ExpenseRowDtoSchema).min(1),
});
export type ExpenseGroupRowDto = z.infer<typeof ExpenseGroupRowDtoSchema>;

/** What the table renders: an entered expense, a derived mortgage line, or a group of either. */
export const ExpenseListRowDtoSchema = z.union([ExpenseRowDtoSchema, ExpenseGroupRowDtoSchema]);
export type ExpenseListRowDto = z.infer<typeof ExpenseListRowDtoSchema>;

/**
 * One line of the settle-up: what one member should transfer to another each month.
 *
 * Netted and directional. A pair coming to zero produces no line at all, so there is nothing for a client to
 * filter and no negative amount to render (FR-066).
 */
export const SettleUpLineDtoSchema = z.strictObject({
  debtorId: z.uuid(),
  debtorName: z.string(),
  creditorId: z.uuid(),
  creditorName: z.string(),
  amountInEuros: z.number().positive(),
});
export type SettleUpLineDto = z.infer<typeof SettleUpLineDtoSchema>;

/**
 * What one member is RESPONSIBLE for per month — their share of the household's outgoings.
 *
 * Not what leaves their account, and not the settle-up: this is the splits, and the settle-up is the difference
 * between the splits and the cards. See `memberShares` in the domain.
 */
export const MemberShareDtoSchema = z.strictObject({
  memberId: z.uuid(),
  displayName: z.string(),
  monthlyInEuros: z.number(),
  /** A share carried forward for somebody no longer active. Rendered recessively, never dropped. */
  isFrozen: z.boolean(),
});
export type MemberShareDto = z.infer<typeof MemberShareDtoSchema>;

export const CategoryTotalDtoSchema = z.strictObject({
  category: ExpenseCategorySchema,
  totalInEuros: z.number(),
});

export const ExpensesPageDtoSchema = z.strictObject({
  /** The evaluation date, resolved ONCE for the whole response. */
  asOf: CalendarDateSchema,
  settleUp: z.array(SettleUpLineDtoSchema),
  /**
   * Entered expenses, mortgage parts and GROUPS in one list, sorted by start date (FR-075).
   *
   * A grouped row's children do not also appear here — see `ExpenseGroupRowDtoSchema` for why that
   * is what keeps `monthlyTotalInEuros` from double-counting.
   */
  rows: z.array(ExpenseListRowDtoSchema),
  /** Includes the mortgage rows, at the payment rather than the interest (FR-018). */
  monthlyTotalInEuros: z.number(),
  byMember: z.array(MemberShareDtoSchema),
  /** Only categories with a non-zero total, in catalogue order (FR-020). */
  byCategory: z.array(CategoryTotalDtoSchema),
  cards: z.array(CardDtoSchema),
  /**
   * True when the properties read FAILED, so the mortgage rows are missing and the total is short.
   *
   * The one thing a missing row cannot say for itself. An empty list and a failed read are otherwise
   * indistinguishable, and the difference is money absent with no error.
   */
  mortgageUnavailable: z.boolean(),
});
export type ExpensesPageDto = z.infer<typeof ExpensesPageDtoSchema>;

// ---------------------------------------------------------------------------
// Requests — expenses
// ---------------------------------------------------------------------------

/** No `memberDisplayName`. See the header. */
export const ExpenseSplitInputSchema = z.object({
  memberId: z.uuid(),
  percentage: SplitPercentageSchema,
});
export type ExpenseSplitInput = z.infer<typeof ExpenseSplitInputSchema>;

export const CreateExpenseRequestSchema = z.object({
  description: z.string().trim().min(1).max(200),
  amount: ExpenseAmountSchema,
  interval: RecurrenceIntervalSchema,
  category: ExpenseCategorySchema,
  /** A future date is permitted. The expense contributes nothing until it arrives. */
  startDate: CalendarDateSchema,
  cardId: z.uuid().nullable().optional(),
  /**
   * Omitted or empty means household-shared with no individual attribution — which the settle-up
   * alone treats as an equal share across the current active members, so a shared bill does not have
   * to be hand-entered as 50/50 before the settle-up means anything.
   */
  splits: z.array(ExpenseSplitInputSchema).optional(),
  /**
   * When a payment is debited (feature 024). Absent or null means undated — a legitimate state, never
   * filled in from `startDate`, which is when the commitment began and not when it is debited (FR-002).
   */
  firstPaymentDate: CalendarDateSchema.nullish(),
});
export type CreateExpenseRequest = z.infer<typeof CreateExpenseRequestSchema>;

/**
 * Every field optional. Omitting `splits` leaves the stored set untouched; sending `[]` clears it to
 * household-shared. The two are deliberately different, because "I am editing the amount" and "this
 * cost is nobody's in particular" are different intentions and one request shape has to carry both.
 */
export const UpdateExpenseRequestSchema = z.object({
  description: z.string().trim().min(1).max(200).optional(),
  amount: ExpenseAmountSchema.optional(),
  interval: RecurrenceIntervalSchema.optional(),
  category: ExpenseCategorySchema.optional(),
  startDate: CalendarDateSchema.optional(),
  cardId: z.uuid().nullable().optional(),
  splits: z.array(ExpenseSplitInputSchema).optional(),
  /**
   * ABSENT, A DATE AND `null` ARE THREE DIFFERENT REQUESTS (feature 024): unchanged, set or change, and
   * clear. Changing `interval` on an expense that has a date in force REQUIRES this field — a date or an
   * explicit `null` — or the edit is refused with `EXP_FIRST_PAYMENT_DATE_REQUIRED` (FR-006, research R13).
   */
  firstPaymentDate: CalendarDateSchema.nullable().optional(),
});
export type UpdateExpenseRequest = z.infer<typeof UpdateExpenseRequestSchema>;

// ---------------------------------------------------------------------------
// Requests — cards
// ---------------------------------------------------------------------------

/**
 * No `ownerDisplayName`, and no field of any kind for a full card or account number.
 *
 * The absence is the requirement rather than an omission: sending one is a Zod rejection, not a value
 * that is quietly discarded.
 */
export const CreateCardRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
  ownership: CardOwnershipSchema,
  /** Required when `individual`, forbidden when `joint` — enforced in the domain, which can say why. */
  ownerId: z.uuid().nullable().optional(),
  bankCode: BankCodeSchema.nullable().optional(),
  lastFour: LastFourSchema.nullable().optional(),
});
export type CreateCardRequest = z.infer<typeof CreateCardRequestSchema>;

export const UpdateCardRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  ownership: CardOwnershipSchema.optional(),
  ownerId: z.uuid().nullable().optional(),
  bankCode: BankCodeSchema.nullable().optional(),
  lastFour: LastFourSchema.nullable().optional(),
});
export type UpdateCardRequest = z.infer<typeof UpdateCardRequestSchema>;

// ---------------------------------------------------------------------------
// Requests — the attribution of a derived line
// ---------------------------------------------------------------------------

/**
 * Which card pays a mortgage-interest line, and whose cost it is.
 *
 * NO AMOUNT, AND NO FIELD THAT COULD CARRY ONE. The figure is computed from the mortgage part on every
 * read and stored nowhere; this request attributes it. That is what keeps the line read-only in the sense
 * that matters — nobody types the mortgage figure — while letting the household say whose cost it is.
 *
 * `cardId` may be null: "we have decided whose cost this is, but not which account it leaves from". The
 * split still displays, and the settle-up still cannot use it, because there is no funding member.
 *
 * `splits` empty means the household's, which the settle-up reads as an equal share across the current
 * active members — the same meaning an expense with no split carries.
 */
export const AttributeDerivedLineRequestSchema = z.object({
  cardId: z.uuid().nullable(),
  splits: z.array(ExpenseSplitInputSchema),
});
export type AttributeDerivedLineRequest = z.infer<typeof AttributeDerivedLineRequestSchema>;

// ---------------------------------------------------------------------------
// Query parameters
// ---------------------------------------------------------------------------

/**
 * All optional and combinable. They filter the `expenses` array ONLY.
 *
 * The totals and the settle-up stay computed over the unfiltered active set, because a filtered
 * settle-up answers a different question from the one the panel asks — "what does Anouk owe on the
 * subscriptions" is not a transfer anybody makes.
 */
export const ExpenseListQuerySchema = z.object({
  category: ExpenseCategorySchema.optional(),
  /** A card id, or the literal `none` for expenses carrying no card. */
  cardId: z.union([z.uuid(), z.literal("none")]).optional(),
  /**
   * WHOSE COST, and not who paid for it — a member id, or the literal `joint`.
   *
   * This filters on the SPLIT, which is the same axis the `byMember` card decomposes the total on. A
   * member id selects every row they hold a stored share of; `joint` selects the rows with no split
   * recorded at all, which is the household-shared set.
   *
   * It is deliberately NOT the settle-up's equal-share rule. That rule reads an absent split as
   * "everybody, evenly" so a transfer can be computed without anyone hand-entering 50/50 — but a
   * filter answering "what is Anouk's" by returning every unsplit expense as well would return the
   * whole list on a household that has never entered a split, which is the opposite of narrowing.
   * `joint` exists so that set is still askable, in one place, under its own name.
   */
  member: z.union([z.uuid(), z.literal("joint")]).optional(),
  state: z.enum(["active", "archived"]).optional(),
});
export type ExpenseListQuery = z.infer<typeof ExpenseListQuerySchema>;

/**
 * What the trend line is about, when it is about less than the whole household.
 *
 * TWO OF THE LIST'S FOUR. `state` has no meaning on a series that already reconstructs what was live in
 * each month, and `cardId` is refused because nothing records which card paid a bill in March — a card
 * filter would apply today's attribution to twelve months and draw a past that never happened. See
 * `ExpensesChartFilters` in the domain for the same argument at length, including why `member` is
 * accepted under the same caveat rather than refused with it.
 */
export const ExpensesChartQuerySchema = z.object({
  category: ExpenseCategorySchema.optional(),
  member: z.union([z.uuid(), z.literal("joint")]).optional(),
});
export type ExpensesChartQuery = z.infer<typeof ExpensesChartQuerySchema>;

// ---------------------------------------------------------------------------
// When payments are debited — feature 024
// ---------------------------------------------------------------------------

/**
 * A mortgage row's first payment date, on its own endpoint (research R10).
 *
 * Not a field on `AttributeDerivedLineRequestSchema`: a date-only save would then create an attribution row
 * and log "set who pays" for a change that set nobody (FR-008).
 */
export const DerivedPaymentDateRequestSchema = z.object({
  firstPaymentDate: CalendarDateSchema.nullable(),
});
export type DerivedPaymentDateRequest = z.infer<typeof DerivedPaymentDateRequestSchema>;

export const SchedulePeriodSchema = z.enum(["week", "month", "year"]);
export type SchedulePeriod = z.infer<typeof SchedulePeriodSchema>;

/** The chart endpoint's scope parameter exactly, so the page's one filter means one thing in both reads. */
export const ExpensesScheduleQuerySchema = z.object({
  period: SchedulePeriodSchema.default("month"),
  member: z.union([z.uuid(), z.literal("joint")]).optional(),
});
export type ExpensesScheduleQuery = z.infer<typeof ExpensesScheduleQuerySchema>;

export const SchedulePaymentDtoSchema = z.strictObject({
  /** The expense id, or `mortgage:<sourceId>` for a derived line. */
  rowKey: z.string(),
  description: z.string(),
  card: ExpenseCardRefSchema.nullable(),
  amountInEuros: z.number(),
});

export const ScheduleStepDtoSchema = z.strictObject({
  date: CalendarDateSchema,
  totalInEuros: z.number(),
  payments: z.array(SchedulePaymentDtoSchema),
});

export const UndatedEstimateDtoSchema = z.strictObject({
  rowKey: z.string(),
  kind: z.enum(["expense", "mortgage"]),
  description: z.string(),
  interval: RecurrenceIntervalSchema,
  /** ALWAYS an estimate — the period's average, rendered with ≈ (FR-021). */
  estimateInEuros: z.number(),
});

/**
 * `GET /api/expenses/schedule` (contracts/schedule-api.md).
 *
 * CASH DUE, never a monthly equivalent — except `undated`, whose figures are averages and say so. The
 * invariants the integration test holds this to: `scheduled = Σ steps`, `remaining = Σ steps on or after
 * asOf`, `undatedEstimate = Σ undated`, `periodTotal = scheduled + undatedEstimate`, and `isEstimate` true
 * exactly when `undated` is non-empty.
 */
export const ExpensesScheduleDtoSchema = z.strictObject({
  /** The household's today (Europe/Amsterdam), which is what "still to go out" is measured from. */
  asOf: CalendarDateSchema,
  period: SchedulePeriodSchema,
  window: z.strictObject({ from: CalendarDateSchema, to: CalendarDateSchema }),
  totals: z.strictObject({
    scheduledInEuros: z.number(),
    remainingInEuros: z.number(),
    undatedEstimateInEuros: z.number(),
    periodTotalInEuros: z.number(),
    isEstimate: z.boolean(),
  }),
  steps: z.array(ScheduleStepDtoSchema),
  undated: z.array(UndatedEstimateDtoSchema),
  /** Every active row's next due date on or after `asOf`, whatever the period. Null when undated. */
  nextDue: z.record(z.string(), CalendarDateSchema.nullable()),
  /** The first payment date in force on `asOf` — what the form and the "Who pays" dialog prefill. */
  currentAnchor: z.record(z.string(), CalendarDateSchema.nullable()),
  mortgageUnavailable: z.boolean(),
});
export type ExpensesScheduleDto = z.infer<typeof ExpensesScheduleDtoSchema>;
